/**
 * RoleApprovedGuard — gates a network role's active tools (dashboard,
 * earnings, recruits, invite tools, manage, etc.) behind an approved /
 * active agent_profiles row for that role.
 *
 * Behavior:
 *   - loading            → spinner
 *   - logged-out         → redirect to /auth
 *   - approved/active    → render children
 *   - pending|rejected|none → redirect to /{role landing}
 *
 * This does NOT touch onboarding/welcome/activate/landing or any approval
 * RPC. It only prevents pending applicants from accessing role-specific
 * active tools before approval.
 */
import React from 'react';
import { Navigate } from 'react-router-dom';
import { Spinner } from '@bright-web-skeleton/ui';
import { useRoleStatus, type NetworkRole } from '../../hooks/useRoleStatus';

const ROLE_LANDING: Record<NetworkRole, string> = {
  connector: '/connector',
  agent: '/agent',
  general_agent: '/general-agent',
};

interface RoleApprovedGuardProps {
  role: NetworkRole;
  children: React.ReactNode;
}

export const RoleApprovedGuard: React.FC<RoleApprovedGuardProps> = ({ role, children }) => {
  const { status } = useRoleStatus(role);

  if (status === 'loading') {
    return (
      <div className="min-h-[50vh] flex items-center justify-center">
        <Spinner size="lg" />
      </div>
    );
  }

  if (status === 'logged-out') {
    return <Navigate to="/auth" replace />;
  }

  if (status !== 'approved') {
    return <Navigate to={ROLE_LANDING[role]} replace />;
  }

  return <>{children}</>;
};

export default RoleApprovedGuard;